import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  REPEAT_WEEKLY, REPEAT_DAILY, REPEAT_ONCE,
  DAY_NAMES, ALL_DAYS_MASK, MIN_M, MAX_M, STEP_M, QUICK_DISTANCES,
  autoEndMin, minFinishMin, secToLabel, labelToSec, addMinutesLabel,
  findConflict, validateRace,
} from '../../lib/raceConflict';

/* 경기 추가 · 수정 모달.
   상세: docs/race/제타트랙_레이스모드_작업계획서.md §6.2 */

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(17, 24, 39, 0.45);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 20px; overflow: auto;
`;

const Modal = styled.div`
  width: 100%; max-width: 820px; margin: auto;
  background: #fff; border-radius: 10px;
  display: flex; flex-direction: column;
  max-height: calc(100vh - 40px);
`;

const Head = styled.div`
  padding: 15px 20px; border-bottom: 1px solid #e5e7eb;
  display: flex; align-items: center; justify-content: space-between;
  font-weight: 600; font-size: 16px;
`;

const Body = styled.div`padding: 20px; overflow: auto;`;

const Grid = styled.div`
  display: grid; gap: 14px;
  grid-template-columns: repeat(${(p) => p.cols || 4}, minmax(0, 1fr));
  @media (max-width: 1080px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const Label = styled.label`
  display: block; font-size: 13px; font-weight: 600;
  color: #6b7280; margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%; padding: 9px 11px; font-size: 14px;
  border: 1px solid #e5e7eb; border-radius: 7px; outline: none; box-sizing: border-box;
  &:focus { border-color: #2563eb; }
`;

const Select = styled.select`
  padding: 9px 11px; font-size: 14px;
  border: 1px solid #e5e7eb; border-radius: 7px; outline: none;
`;

const Stepper = styled.div`
  display: flex; align-items: center;
  border: 1px solid #e5e7eb; border-radius: 7px; overflow: hidden;

  input { border: 0; border-radius: 0; text-align: center; }
  button {
    width: 36px; height: 38px; flex: none;
    background: #f8f9fa; border: 0; cursor: pointer; font-size: 16px; color: #374151;
    &:hover { background: #f3f4f6; }
  }
`;

const Unit = styled.span`padding: 0 11px; color: #6b7280; font-size: 13px; flex: none;`;

const Row = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;

const Hint = styled.p`
  margin: 8px 0 0; font-size: 13px; line-height: 1.6; color: #6b7280;
  b { color: ${(p) => p.tone === 'bad' ? '#991b1b' : '#111827'}; }
`;

const Chip = styled.button`
  padding: 6px 12px; font-size: 13px; cursor: pointer;
  border-radius: 999px;
  border: 1px solid ${(p) => p.on ? '#3b82f6' : '#e5e7eb'};
  background: ${(p) => p.on ? '#eff6ff' : '#fff'};
  color: ${(p) => p.on ? '#1d4ed8' : '#374151'};
  &:hover { border-color: #d1d5db; }
`;

const DayBtn = styled.button`
  width: 38px; height: 34px; font-size: 13px; cursor: pointer;
  border-radius: 7px;
  border: 1px solid ${(p) => p.on ? '#3b82f6' : '#e5e7eb'};
  background: ${(p) => p.on ? '#eff6ff' : '#fff'};
  color: ${(p) => p.on ? '#1d4ed8' : '#6b7280'};
  font-weight: ${(p) => p.on ? 700 : 400};
`;

const Alert = styled.div`
  display: flex; gap: 9px; align-items: flex-start;
  padding: 11px 14px; border-radius: 6px; font-size: 13px; margin-top: 16px;
  background: ${(p) => p.err ? '#fef2f2' : '#fffbeb'};
  color: ${(p) => p.err ? '#991b1b' : '#92400e'};
  border: 1px solid ${(p) => p.err ? '#fca5a5' : '#fde68a'};
`;

const Foot = styled.div`
  display: flex; gap: 8px; margin-top: 20px; padding-top: 18px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button`
  padding: 9px 16px; font-size: 14px; font-weight: 600; border-radius: 7px; cursor: pointer;
  color: ${(p) => p.primary ? '#fff' : '#374151'};
  background: ${(p) => p.primary ? '#2563eb' : '#fff'};
  border: 1px solid ${(p) => p.primary ? '#2563eb' : '#e5e7eb'};
  &:hover:not(:disabled) { background: ${(p) => p.primary ? '#1d4ed8' : '#f8f9fa'}; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const SectionTitle = styled.div`
  font-size: 13px; font-weight: 600; color: #6b7280; margin: 22px 0 8px;
`;

const todayYmd = () => {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
};

const ymdToInput = (ymd) => {
  const s = String(ymd || '');
  return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : '';
};
const inputToYmd = (v) => Number(String(v || '').replace(/-/g, '')) || 0;

const EMPTY = {
  startTime: labelToSec('18:15'),
  distance: 1000,
  endMin: autoEndMin(1000),
  repeatType: REPEAT_WEEKLY,
  repeatDays: (1 << 1) | (1 << 3) | (1 << 5),   // 월·수·금
  onceDate: todayYmd(),
  minMember: 2,
  prize1: 500, prize2: 200, prize3: 100,
  giftPoint: 0,
  active: 1,
};

const RaceScheduleModal = ({ open, editing, schedules, onSave, onClose, saving }) => {
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setErr('');
    setForm(editing ? { ...EMPTY, ...editing } : { ...EMPTY });
  }, [open, editing]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  /* 거리를 바꾸면 종료 시간을 항상 다시 계산해 덮어쓴다.
     거리만 고치고 종료 시간이 옛 값으로 남으면 둘이 조용히 어긋난다. */
  const setDistance = (meters) => {
    const m = Math.min(MAX_M, Math.max(MIN_M, Math.round(meters / STEP_M) * STEP_M));
    set({ distance: m, endMin: autoEndMin(m) });
  };

  const toggleDay = (d) => set({ repeatDays: (form.repeatDays ^ (1 << d)) & ALL_DAYS_MASK });

  const conflict = findConflict(form, schedules, editing ? editing.clubRaceScheduleID : null);
  const floor = minFinishMin(form.distance);
  const auto = autoEndMin(form.distance);
  const tooShort = Number(form.endMin) < floor;

  const handleSave = () => {
    const msg = validateRace(form);
    if (msg) { setErr(msg); return; }
    if (conflict) {
      setErr(`${secToLabel(conflict.startTime)} ${Number(conflict.distance).toLocaleString()}m 경기와 시간이 겹칩니다.`);
      return;
    }
    setErr('');
    onSave(form);
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <Head>
          <span>{editing ? '경기 수정' : '경기 추가'}</span>
          <Button onClick={onClose}>닫기</Button>
        </Head>
        <Body>
          <Grid cols={4}>
            <div>
              <Label htmlFor="race-time">시작 시간</Label>
              <Input
                id="race-time" type="time" step="300"
                value={secToLabel(form.startTime)}
                onChange={(e) => set({ startTime: labelToSec(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="race-dist">거리 (m)</Label>
              <Stepper>
                <button type="button" aria-label="100m 줄이기" onClick={() => setDistance(Number(form.distance) - STEP_M)}>−</button>
                <Input
                  id="race-dist" type="number" min={MIN_M} max={MAX_M} step={STEP_M}
                  value={form.distance}
                  onChange={(e) => set({ distance: Number(e.target.value) || 0, endMin: autoEndMin(e.target.value) })}
                />
                <button type="button" aria-label="100m 늘리기" onClick={() => setDistance(Number(form.distance) + STEP_M)}>＋</button>
              </Stepper>
            </div>
            <div>
              <Label htmlFor="race-min">최소 참여 인원</Label>
              <Input
                id="race-min" type="number" min="1" max="20"
                value={form.minMember}
                onChange={(e) => set({ minMember: Number(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label htmlFor="race-end">경기 종료 시간</Label>
              <Stepper>
                <Input
                  id="race-end" type="number" min="1" max="180"
                  value={form.endMin}
                  onChange={(e) => set({ endMin: Number(e.target.value) || 0 })}
                />
                <Unit>분</Unit>
              </Stepper>
            </div>
          </Grid>

          <Hint tone={tooShort ? 'bad' : ''}>
            {tooShort ? (
              <b>이 거리는 가장 빨리 달려도 {floor}분이 걸립니다. 아무도 완주하지 못합니다.</b>
            ) : (
              <>
                {secToLabel(form.startTime)} 시작 · <b>{addMinutesLabel(form.startTime, form.endMin)} 종료</b>
                {' '}— 완주하지 못한 참가자가 있어도 이 시각에 경기를 끝냅니다.
                {Number(form.endMin) === auto ? ' (100m당 1분 기준)' : ` (기본값 ${auto}분에서 조정됨)`}
              </>
            )}
          </Hint>

          <SectionTitle>자주 쓰는 거리</SectionTitle>
          <Row>
            {QUICK_DISTANCES.map((m) => (
              <Chip key={m} type="button" on={Number(form.distance) === m} onClick={() => setDistance(m)}>
                {m.toLocaleString()}m
              </Chip>
            ))}
          </Row>

          <SectionTitle>반복</SectionTitle>
          <Row>
            <Select value={form.repeatType} onChange={(e) => set({ repeatType: Number(e.target.value) })}>
              <option value={REPEAT_WEEKLY}>매주</option>
              <option value={REPEAT_DAILY}>매일</option>
              <option value={REPEAT_ONCE}>1회만</option>
            </Select>
            {Number(form.repeatType) === REPEAT_WEEKLY && (
              <Row>
                {DAY_NAMES.map((name, d) => (
                  <DayBtn key={d} type="button" on={!!(form.repeatDays & (1 << d))} onClick={() => toggleDay(d)}>
                    {name}
                  </DayBtn>
                ))}
              </Row>
            )}
            {Number(form.repeatType) === REPEAT_ONCE && (
              <Input
                type="date" style={{ width: 180 }}
                value={ymdToInput(form.onceDate)}
                onChange={(e) => set({ onceDate: inputToYmd(e.target.value) })}
              />
            )}
          </Row>

          <SectionTitle>상금</SectionTitle>
          <Grid cols={3}>
            <div>
              <Label htmlFor="race-p1">1등 포인트</Label>
              <Input id="race-p1" type="number" min="0" step="10" value={form.prize1}
                onChange={(e) => set({ prize1: Number(e.target.value) || 0 })} />
            </div>
            <div>
              <Label htmlFor="race-p2">2등 포인트</Label>
              <Input id="race-p2" type="number" min="0" step="10" value={form.prize2}
                onChange={(e) => set({ prize2: Number(e.target.value) || 0 })} />
            </div>
            <div>
              <Label htmlFor="race-p3">3등 포인트</Label>
              <Input id="race-p3" type="number" min="0" step="10" value={form.prize3}
                onChange={(e) => set({ prize3: Number(e.target.value) || 0 })} />
            </div>
          </Grid>

          {conflict && (
            <Alert>
              <span>⚠️</span>
              <span>
                <b>{secToLabel(conflict.startTime)} {Number(conflict.distance).toLocaleString()}m</b> 경기와
                시간이 겹칩니다. 시작 시간이나 거리를 조정하세요.
              </span>
            </Alert>
          )}
          {err && <Alert err><span>✕</span><span>{err}</span></Alert>}

          <Foot>
            <Button primary disabled={!!conflict || saving} onClick={handleSave}>
              {saving ? '저장 중...' : '저장'}
            </Button>
            <Button onClick={onClose}>취소</Button>
            <span style={{ margin: 'auto 0 auto auto', fontSize: 13, color: '#6b7280' }}>
              이 경기 1회 최대 지급{' '}
              {((Number(form.prize1) || 0) + (Number(form.prize2) || 0) + (Number(form.prize3) || 0)).toLocaleString()}P
            </span>
          </Foot>
        </Body>
      </Modal>
    </Overlay>
  );
};

export default RaceScheduleModal;
